import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";

import SuggestionsModal from "@/pages/assessment/SuggestionsModal";
import ActionModal from "@/pages/assessment/ActionModal";
import OcrImportModal from "@/pages/assessment/OcrImportModal";
import BulkInviteModal from "@/pages/assessment/BulkInviteModal";
import InvitesTab from "@/pages/assessment/InvitesTab";
import { OverviewTab, ResultsTab, ActionsTab, ResponsesTab } from "@/pages/assessment/Tabs";
import AssessmentAIPanel from "@/pages/assessment/AssessmentAIPanel";

const INSTRUMENT_META = {
  nr01_54: { name: "NR-01 Sollute · 54q", icon: "fa-leaf", color: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  rapido:  { name: "Teste Rápido NR-01 · 18q", icon: "fa-bolt-lightning", color: "bg-amber-50 text-amber-800 border-amber-200" },
  copsoq:  { name: "COPSOQ · 40q", icon: "fa-magnifying-glass-chart", color: "bg-indigo-50 text-indigo-800 border-indigo-200" },
  hse:     { name: "HSE Management · 35q", icon: "fa-shield-halved", color: "bg-rose-50 text-rose-800 border-rose-200" },
};

function RiskBadge({ level, label }) {
  const cls = level === 0 ? "risk-0" : `risk-${level}`;
  return <span className={`${cls} border px-2.5 py-1 text-xs font-bold uppercase tracking-wider`}>{label}</span>;
}

async function copyToClipboard(text) {
  // Primary path: modern Async Clipboard API. May be denied in sandboxed
  // iframes (Permissions-Policy "clipboard-write"). Silently fall through
  // to the textarea+execCommand fallback below.
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_err) {
    // Primary path denied (sandboxed iframe / Permissions-Policy "clipboard-write").
    // Falls through to the textarea+execCommand fallback below — expected, not an error.
  }
  // Fallback path: ephemeral textarea + execCommand("copy").
  // Caller (the copy button) toasts on `false`.
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed"; ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus(); ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (_err) {
    // Both clipboard paths unavailable — caller will surface a user-facing toast.
    return false;
  }
}

export default function AssessmentDetail() {
  const { id } = useParams();
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [results, setResults] = useState(null);
  const [actions, setActions] = useState([]);
  const [responses, setResponses] = useState([]);
  const [showAction, setShowAction] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showOcr, setShowOcr] = useState(false);
  const [showBulkInvite, setShowBulkInvite] = useState(false);
  // Bumped after a successful bulk invite so InvitesTab re-mounts and refetches.
  const [invitesRefreshKey, setInvitesRefreshKey] = useState(0);

  const publicUrl = useMemo(() => data ? `${window.location.origin}/q/${data.public_token}` : "", [data]);

  const load = useCallback(async () => {
    const [a, r, p] = await Promise.all([
      api.get(`/assessments/${id}`),
      api.get(`/assessments/${id}/results`),
      api.get(`/assessments/${id}/action-plans`),
    ]);
    setData(a.data); setResults(r.data); setActions(p.data);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const reloadActions = useCallback(async () => {
    const p = await api.get(`/assessments/${id}/action-plans`);
    setActions(p.data);
  }, [id]);

  const reloadResponses = useCallback(async () => {
    const r = await api.get(`/assessments/${id}/responses`);
    setResponses(r.data);
  }, [id]);

  const refreshResults = async () => {
    const r = await api.get(`/assessments/${id}/results`);
    setResults(r.data);
    toast.success("Resultados atualizados");
  };

  const copyLink = async () => {
    const ok = await copyToClipboard(publicUrl);
    if (ok) toast.success("Link copiado!");
    else toast.info("Selecione e copie o link manualmente.");
  };

  const updateAction = async (a, patch) => {
    const payload = { ...a, ...patch };
    // optimistic
    setActions((prev) => prev.map((x) => (x.id === a.id ? { ...x, ...patch } : x)));
    try {
      await api.put(`/action-plans/${a.id}`, payload);
      toast.success("Ação atualizada");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
      reloadActions(); // rollback by reloading
    }
  };

  const delAction = async (aid) => {
    try {
      await api.delete(`/action-plans/${aid}`);
      setActions((prev) => prev.filter((x) => x.id !== aid));
      toast.success("Ação removida");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify({ assessment: data, results, actions }, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${data.titulo.replace(/\s+/g, "_")}.json`;
    a.click();
  };

  const exportCsv = async () => {
    const r = await api.get(`/assessments/${id}/responses`);
    const rows = [["respondente","matricula","submetido_em","questao","dimensao","valor"]];
    r.data.forEach((resp) => {
      resp.answers.forEach((ans) => {
        const q = results.questions.find((qq) => qq.id === ans.question_id);
        rows.push([
          resp.employee_name || "anônimo",
          resp.employee_matricula || "",
          resp.submitted_at,
          q?.pergunta || ans.question_id,
          q?.dimensao || "",
          ans.value,
        ]);
      });
    });
    const csv = rows.map((row) => row.map((c) => `"${String(c).replaceAll('"','""')}"`).join(";")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${data.titulo.replace(/\s+/g, "_")}_respostas.csv`;
    a.click();
  };

  if (!data || !results) {
    return <div className="p-12 text-slate-500"><i className="fa-solid fa-circle-notch fa-spin mr-2"></i> Carregando…</div>;
  }

  const tabs = [
    ["overview", "Visão geral", "fa-gauge"],
    ["results", "Resultados", "fa-chart-column"],
    ["actions", `Plano de ação (${actions.length})`, "fa-list-check"],
    ["responses", `Respostas (${data.response_count})`, "fa-users"],
    ["invites", "Convites", "fa-paper-plane"],
  ];

  return (
    <div className="p-8 lg:p-12 max-w-7xl mx-auto">
      <Link to="/app/avaliacoes" className="text-sm text-slate-500 hover:text-emerald-900" data-testid="detail-back-link">
        ← Voltar para avaliações
      </Link>

      <div className="mt-4 flex flex-wrap justify-between items-end gap-4 mb-8">
        <div>
          <div className="overline mb-2">{data.company_snapshot?.razao_social}</div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">{data.titulo}</h1>
          <div className="text-slate-600 mt-2">
            GHE/Setor: <strong>{data.ghe_setor || "—"}</strong> · Função: <strong>{data.funcao || "—"}</strong> ·
            Respondentes esperados: <strong>{data.n_trabalhadores || 0}</strong>
          </div>
          {(() => {
            const meta = INSTRUMENT_META[data.test_type || "nr01_54"] || INSTRUMENT_META.nr01_54;
            return (
              <div className={`mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border ${meta.color}`} data-testid="instrument-badge">
                <i className={`fa-solid ${meta.icon}`}></i>
                <span className="text-xs font-semibold uppercase tracking-wider">Instrumento</span>
                <span className="text-sm font-bold">{meta.name}</span>
              </div>
            );
          })()}
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowOcr(true)} className="btn btn-secondary" data-testid="detail-ocr-button">
            <i className="fa-solid fa-file-import"></i> Importar digitalizado
          </button>
          <Link
            to={`/imprimir/${id}?layout=table`}
            target="_blank"
            className="btn btn-secondary"
            data-testid="detail-print-table"
          >
            <i className="fa-solid fa-table-list"></i> Imprimir — tabela 1-5
          </Link>
          <Link
            to={`/imprimir/${id}?layout=labeled`}
            target="_blank"
            className="btn btn-secondary"
            data-testid="detail-print-labeled"
          >
            <i className="fa-solid fa-list-ul"></i> Imprimir — respostas escritas
          </Link>
          <button onClick={refreshResults} className="btn btn-secondary" data-testid="detail-refresh-button">
            <i className="fa-solid fa-arrows-rotate"></i> Atualizar
          </button>
          <Link to={`/aep/${id}`} className="btn btn-primary" data-testid="detail-aep-link">
            <i className="fa-solid fa-file-pdf"></i> Relatório AEP
          </Link>
        </div>
      </div>

      <div className="card mb-6 border-emerald-200 bg-emerald-50">
        <div className="overline text-emerald-900 mb-3">Link anônimo do colaborador</div>
        <div className="flex flex-wrap items-center gap-3">
          <input readOnly value={publicUrl} className="input font-mono text-xs flex-1 min-w-[300px]" data-testid="detail-public-link-input" />
          <button onClick={copyLink} className="btn btn-primary" data-testid="detail-copy-link-button">
            <i className="fa-solid fa-copy"></i> Copiar
          </button>
          <a href={publicUrl} target="_blank" rel="noreferrer" className="btn btn-secondary" data-testid="detail-open-link">
            <i className="fa-solid fa-up-right-from-square"></i> Abrir
          </a>
          <button onClick={() => setShowBulkInvite(true)} className="btn btn-secondary"
                  data-testid="detail-bulk-invite-button">
            <i className="fa-solid fa-paper-plane"></i> Convidar por e-mail
          </button>
        </div>
        <p className="text-xs text-emerald-900/80 mt-3">
          Envie esse link aos colaboradores. As respostas são <strong>anônimas</strong> e atualizam o painel em tempo real.
        </p>
      </div>

      <AssessmentAIPanel assessmentId={id} responseCount={data.response_count || 0} />

      {showBulkInvite && (
        <BulkInviteModal
          assessmentId={id}
          onClose={() => setShowBulkInvite(false)}
          onSuccess={() => setInvitesRefreshKey((k) => k + 1)}
        />
      )}

      <div className="border-b border-slate-200 mb-6 flex gap-2 flex-wrap">
        {tabs.map(([k, label, icon]) => (
          <button key={k}
            onClick={() => { setTab(k); if (k === "responses") reloadResponses(); }}
            data-testid={`tab-${k}`}
            className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
              tab === k ? "border-emerald-900 text-emerald-900" : "border-transparent text-slate-500 hover:text-slate-900"
            }`}>
            <i className={`fa-solid ${icon} mr-2`}></i>{label}
          </button>
        ))}
        <div className="ml-auto flex gap-2 pb-2">
          <button onClick={exportJson} className="btn btn-secondary text-xs" data-testid="export-json"><i className="fa-solid fa-file-code"></i> JSON</button>
          <button onClick={exportCsv} className="btn btn-secondary text-xs" data-testid="export-csv"><i className="fa-solid fa-file-csv"></i> CSV</button>
        </div>
      </div>

      {tab === "overview" && <OverviewTab data={data} results={results} actions={actions} />}
      {tab === "results" && <ResultsTab results={results} assessmentId={id} assessment={data} />}
      {tab === "actions" && (
        <ActionsTab
          actions={actions}
          onDelete={delAction}
          onNew={() => setShowAction(true)}
          onSuggestions={() => setShowSuggestions(true)}
          onUpdate={updateAction}
        />
      )}
      {tab === "responses" && <ResponsesTab responses={responses} totalQuestions={results?.questions?.length} />}
      {tab === "invites" && (
        <InvitesTab
          key={invitesRefreshKey}
          assessmentId={id}
          onOpenInviteModal={() => setShowBulkInvite(true)}
        />
      )}

      {showAction && (
        <ActionModal
          assessmentId={id}
          dimensions={results.dimensions}
          onClose={() => setShowAction(false)}
          onSaved={() => { setShowAction(false); reloadActions(); }}
        />
      )}
      {showSuggestions && (
        <SuggestionsModal
          assessmentId={id}
          onClose={() => setShowSuggestions(false)}
          onApplied={() => { setShowSuggestions(false); reloadActions(); }}
        />
      )}
      {showOcr && (
        <OcrImportModal
          assessmentId={id}
          testType={data?.test_type}
          onClose={() => setShowOcr(false)}
          onImported={() => { setShowOcr(false); load(); }}
        />
      )}
    </div>
  );
}

// Re-export for downstream tests (if any)
export { RiskBadge };
